import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { IconPlus } from "@/components/Icons";
import { Button, MenuItem, MenuLabel, Popover } from "@/components/kit";
import { addProgrammeAction } from "@/lib/actions/events";
import { displayName } from "@/lib/format";
import type { LevelRow } from "@/lib/api/types";

/** One sport that could go into this Games, and what it may start as. */
export type Addable = {
  sportCode: string;
  /** The grouping this sport sits in, when it has one — Aquatics, for swimming.
   *  Shown as a heading so the disciplines read as belonging together. */
  partOf?: string | null;
  levels: LevelRow[];
};

/**
 * Put a sport inside a Games.
 *
 * Two things this has to get right, both learned the hard way.
 *
 * A programme is an ordinary node of the sport's own tree, so it has to be one
 * of that sport's own top levels — a festival for horse racing, a meet for
 * swimming. It used to be written as the literal "programme", a level no sport
 * declares, and so a node no rule understood: nothing could say what it held,
 * and no parent picker offered it. Adding a meeting "inside" one silently filed
 * it at the root instead.
 *
 * Most sports declare exactly one such level, and are added in a single click.
 * Cricket and horse racing declare two, and only those two ask a second
 * question — nobody is asked something with one possible answer.
 *
 * A grouping sport is not offered at all: Aquatics declares no levels because
 * nothing is ever entered into it. Its disciplines are offered in its place,
 * under its name, because swimming is what actually holds the fixtures.
 */
export function AddProgramme({
  code,
  sports,
  label = "Add a sport",
}: {
  code: string;
  sports: Addable[];
  label?: string;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  /** The sport whose level is being chosen, when it has more than one. */
  const [choosing, setChoosing] = useState<Addable | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (sports.length === 0) return null;

  async function add(sport: Addable, level?: string) {
    setBusy(true);
    setError(null);
    try {
      const result = await addProgrammeAction(
        code,
        sport.sportCode,
        displayName(sport.sportCode),
        level,
      );
      if (result.ok) {
        close();
        navigate(0);
      } else {
        setError(result.error);
      }
    } finally {
      // Release the trigger even if the action rejects at the transport layer.
      setBusy(false);
    }
  }

  function close() {
    setOpen(false);
    setChoosing(null);
    setError(null);
  }

  /** Disciplines under their grouping's name, everything else on its own. */
  const groups = new Map<string, Addable[]>();
  for (const sport of sports) {
    const key = sport.partOf ?? "";
    groups.set(key, [...(groups.get(key) ?? []), sport]);
  }

  return (
    <Popover
      open={open}
      onClose={close}
      align="right"
      trigger={
        <Button
          tone="primary"
          disabled={busy}
          aria-expanded={open}
          onClick={() => (open ? close() : setOpen(true))}
        >
          <IconPlus size={15} />
          {label}
        </Button>
      }
    >
      {choosing ? (
        <>
          <MenuLabel>{displayName(choosing.sportCode)} — as a</MenuLabel>
          {choosing.levels.map((level) => (
            <MenuItem
              key={level.code}
              label={level.label}
              onClick={() => add(choosing, level.code)}
            />
          ))}
        </>
      ) : (
        [...groups.entries()].map(([grouping, members]) => (
          <div key={grouping}>
            <MenuLabel>{grouping ? displayName(grouping) : "Taking part"}</MenuLabel>
            {members.map((sport) => (
              <MenuItem
                key={sport.sportCode}
                label={displayName(sport.sportCode)}
                // One level is not a choice, so it is not offered as one.
                onClick={() =>
                  sport.levels.length > 1 ? setChoosing(sport) : add(sport, sport.levels[0]?.code)
                }
              />
            ))}
          </div>
        ))
      )}
      {error && <MenuLabel>{error}</MenuLabel>}
    </Popover>
  );
}
